
const MERGE_FIELDS = {
  staff: [
    { token: 'title', label: 'Title' },
    { token: 'firstName', label: 'First Name' },
    { token: 'lastName', label: 'Last Name' },
    { token: 'fullName', label: 'Full Name' },
    { token: 'jobTitle', label: 'Job Title' },
    { token: 'campusName', label: 'Campus' },
  ],
  parent: [
    { token: 'title', label: 'Parent Title' },
    { token: 'firstName', label: 'Parent First Name' },
    { token: 'lastName', label: 'Parent Last Name' },
    { token: 'fullName', label: 'Parent Full Name' },
    { token: 'studentName', label: 'Student Name' },
    { token: 'students', label: 'Students (with grade)' },
    { token: 'campusName', label: 'Campus' },
  ],
  student: [
    { token: 'firstName', label: 'Student First Name' },
    { token: 'lastName', label: 'Student Last Name' },
    { token: 'fullName', label: 'Student Full Name' },
    { token: 'campusName', label: 'Campus' },
    { token: 'gradeLevel', label: 'Grade Level' },
    { token: 'gradeSection', label: 'Grade Section' },
  ],
}

const EMAIL_FIELD = { token: 'recipientEmail', label: 'Email' }

export const mergeFieldsFor = (type, channel) => [
  ...(MERGE_FIELDS[type] ?? []),
  ...(channel === 'email' ? [EMAIL_FIELD] : []),
]

export const insertToken = (body, token) => `${body ?? ''}{{${token}}}`


export const substitute = (text, record = {}) =>
  String(text ?? '').replace(/{{\s*(\w+)\s*}}/g, (match, token) => {
    const value = record?.[token]
    return value === undefined || value === null || value === '' ? match : String(value)
  })

export const worstCaseRecord = records => {
  const longest = {}
  for (const record of records ?? []) {
    for (const [token, value] of Object.entries(record ?? {})) {
      const text = value == null ? '' : String(value)
      if (!(token in longest) || text.length > longest[token].length) longest[token] = text
    }
  }
  return longest
}

const GSM = new Set(
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà",
)
const GSM_EXTENDED = new Set('^{}\\[~]|€')

export const smsMetrics = text => {
  const value = text ?? ''
  const unicode = [...value].some(char => !GSM.has(char) && !GSM_EXTENDED.has(char))

  const length = unicode ? [...value].length : [...value].reduce((n, c) => n + (GSM_EXTENDED.has(c) ? 2 : 1), 0)
  const limit = unicode ? 70 : 160

  return { length, limit, unicode, overLimit: length > limit }
}

const BLOCK_TAGS =
  'address|article|aside|blockquote|br|div|dl|dd|dt|fieldset|figcaption|figure|footer|form|h[1-6]|header|hr|li|main|nav|ol|p|pre|section|table|tbody|td|tfoot|th|thead|tr|ul'
const ENDS_WITH_BLOCK = new RegExp(`<\\s*/?(?:${BLOCK_TAGS})\\b[^>]*>\\s*$`, 'i')
const STARTS_WITH_BLOCK = new RegExp(`^\\s*<\\s*/?(?:${BLOCK_TAGS})\\b[^>]*>`, 'i')


export const applyLineBreaks = text => {
  const value = String(text ?? '')

  return value.replace(/\r\n|\r|\n/g, (newline, offset) =>
    ENDS_WITH_BLOCK.test(value.slice(0, offset)) || STARTS_WITH_BLOCK.test(value.slice(offset + newline.length))
      ? newline
      : '<br />',
  )
}

const FONT = 'font-family:Arial,Helvetica,sans-serif'
const FOOTER = `<p style="${FONT};font-size:12px;color:#777;margin:0">Sent from iLEMIS, your school's student management application.</p>`


export const templateLibrary = logos => [
  {
    code: 'plain',
    name: 'Plain',
    description: 'No styling — just your content.',
    wrap: content => content,
  },
  {
    code: 'simple',
    name: 'Simple',
    description: 'Centred column with readable type.',
    wrap: content =>
      `<div style="${FONT};max-width:600px;margin:0 auto;padding:24px;color:#222;line-height:1.5">
  <div>${content}</div>
  <hr style="border:none;border-top:1px solid #e0e0e0;margin:24px 0" />
  ${FOOTER}
</div>`,
  },
  {
    code: 'branded',
    name: 'Branded header',
    description: 'District logo above your content.',
    wrap: content =>
      `<div style="${FONT};max-width:600px;margin:0 auto;color:#222;line-height:1.5">
  <div style="padding:20px 24px;border-bottom:3px solid #1976d2">
    ${logos?.circle ? `<img src="${logos.circle}" alt="" style="display:block;height:44px" />` : ''}
  </div>
  <div style="padding:24px">
    <div>${content}</div>
  </div>
  <div style="padding:16px 24px;background:#fafafa">${FOOTER}</div>
</div>`,
  },
  {
    code: 'card',
    name: 'Card',
    description: 'Content in a bordered card on a tinted background.',
    wrap: content =>
      `<div style="${FONT};background:#f4f6f8;padding:24px">
  <div style="max-width:600px;margin:0 auto;background:#ffffff;border:1px solid #e0e0e0;border-radius:8px;padding:24px;color:#222;line-height:1.5">
    ${logos?.circle ? `<img src="${logos.circle}" alt="" style="display:block;height:40px;margin:0 0 16px" />` : ''}
    <div>${content}</div>
  </div>
  <div style="max-width:600px;margin:12px auto 0;text-align:center">${FOOTER}</div>
</div>`,
  },
  {
    code: 'notice',
    name: 'Notice',
    description: 'Highlighted banner above your content, for urgent messages.',
    wrap: content =>
      `<div style="${FONT};max-width:600px;margin:0 auto;padding:24px;color:#222;line-height:1.5">
  <div style="background:#fdecea;border-left:4px solid #d32f2f;padding:12px 16px;margin-bottom:20px;font-weight:bold">
    Important notice
  </div>
  <div>${content}</div>
  <hr style="border:none;border-top:1px solid #e0e0e0;margin:24px 0" />
  ${FOOTER}
</div>`,
  },
]

export const templateByCode = (logos, code) =>
  templateLibrary(logos).find(template => template.code === code) ?? templateLibrary(logos)[0]
